// app/components/thailand-premium-tapioca-starch-products/OurProducts.tsx
"use client";

import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Card, CardContent, CardHeader, Grid } from "@mui/material";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string;
};

export default function OurProducts2() {
  const t = useTranslations("OurProducts");
  const locale = useLocale();

  const pagePath = "/thailand-premium-tapioca-starch-products";

  // ---- default จาก i18n + path รูปเดิม (SEO/SSR) ----
  const defaultTitle = t("OurProducts.title");

  const defaultItem1Title = t("OurProducts.Item1.title");
  const defaultItem1Weight = t("OurProducts.Item1.weight");
  const defaultItem1Content = t("OurProducts.Item1.content");
  const defaultItem1ContentWeight = t("OurProducts.Item1.content_weight");
  const defaultItem1Image = "/assets/thailand-premium-tapioca-starch-products/our-products/img1.png";

  const defaultItem2Title = t("OurProducts.Item2.title");
  const defaultItem2Weight = t("OurProducts.Item2.weight");
  const defaultItem2Content = t("OurProducts.Item2.content");
  const defaultItem2ContentWeight = t("OurProducts.Item2.content_weight");
  const defaultItem2Image = "/assets/thailand-premium-tapioca-starch-products/our-products/img2.png";

  const defaultItem3Title = t("OurProducts.Item3.title");
  const defaultItem3Weight = t("OurProducts.Item3.weight");
  const defaultItem3Content = t("OurProducts.Item3.content");
  const defaultItem3ContentWeight = t("OurProducts.Item3.content_weight");
  const defaultItem3Image = "/assets/thailand-premium-tapioca-starch-products/our-products/img3.png";

  const defaultItem4Title = t("OurProducts.Item4.title");
  const defaultItem4Weight = t("OurProducts.Item4.weight");
  const defaultItem4Content = t("OurProducts.Item4.content");
  const defaultItem4ContentWeight = t("OurProducts.Item4.content_weight");
  const defaultItem4Image = "/assets/thailand-premium-tapioca-starch-products/our-products/img4.png";

  // ---- state ที่ใช้แสดงจริง (พร้อม override จาก inline) ----
  const [title, setTitle] = React.useState(defaultTitle);

  const [item1Title, setItem1Title] = React.useState(defaultItem1Title);
  const [item1Weight, setItem1Weight] = React.useState(defaultItem1Weight);
  const [item1Content, setItem1Content] = React.useState(defaultItem1Content);
  const [item1ContentWeight, setItem1ContentWeight] = React.useState(
    defaultItem1ContentWeight
  );
  const [item1Image, setItem1Image] = React.useState(defaultItem1Image);

  const [item2Title, setItem2Title] = React.useState(defaultItem2Title);
  const [item2Weight, setItem2Weight] = React.useState(defaultItem2Weight);
  const [item2Content, setItem2Content] = React.useState(defaultItem2Content);
  const [item2ContentWeight, setItem2ContentWeight] = React.useState(
    defaultItem2ContentWeight
  );
  const [item2Image, setItem2Image] = React.useState(defaultItem2Image);

  const [item3Title, setItem3Title] = React.useState(defaultItem3Title);
  const [item3Weight, setItem3Weight] = React.useState(defaultItem3Weight);
  const [item3Content, setItem3Content] = React.useState(defaultItem3Content);
  const [item3ContentWeight, setItem3ContentWeight] = React.useState(
    defaultItem3ContentWeight
  );
  const [item3Image, setItem3Image] = React.useState(defaultItem3Image);

  const [item4Title, setItem4Title] = React.useState(defaultItem4Title);
  const [item4Weight, setItem4Weight] = React.useState(defaultItem4Weight);
  const [item4Content, setItem4Content] = React.useState(defaultItem4Content);
  const [item4ContentWeight, setItem4ContentWeight] = React.useState(
    defaultItem4ContentWeight
  );
  const [item4Image, setItem4Image] = React.useState(defaultItem4Image);

  // locale เปลี่ยน → reset default ก่อน แล้วค่อยให้ inline override
  React.useEffect(() => {
    setTitle(defaultTitle);

    setItem1Title(defaultItem1Title);
    setItem1Weight(defaultItem1Weight);
    setItem1Content(defaultItem1Content);
    setItem1ContentWeight(defaultItem1ContentWeight);
    setItem1Image(defaultItem1Image);

    setItem2Title(defaultItem2Title);
    setItem2Weight(defaultItem2Weight);
    setItem2Content(defaultItem2Content);
    setItem2ContentWeight(defaultItem2ContentWeight);
    setItem2Image(defaultItem2Image);

    setItem3Title(defaultItem3Title);
    setItem3Weight(defaultItem3Weight);
    setItem3Content(defaultItem3Content);
    setItem3ContentWeight(defaultItem3ContentWeight);
    setItem3Image(defaultItem3Image);

    setItem4Title(defaultItem4Title);
    setItem4Weight(defaultItem4Weight);
    setItem4Content(defaultItem4Content);
    setItem4ContentWeight(defaultItem4ContentWeight);
    setItem4Image(defaultItem4Image);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  // //
  React.useEffect(() => {
    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          if (e.cmsKey) {
            map[e.cmsKey] = e.value;
            return;
          }
          // fallback เผื่อมี selector เก่าเก็บ cms-key อยู่
          const match = e.selector?.match(/data-cms-key="([^"]+)"/);
          if (match) {
            map[match[1]] = e.value;
          }
        });

        // ---- apply overrides เฉพาะ key ที่มี ----
        if (map["ourproducts.overview.title"])
          setTitle(map["ourproducts.overview.title"]);

        if (map["ourproducts.overview.item1.title"])
          setItem1Title(map["ourproducts.overview.item1.title"]);
        if (map["ourproducts.overview.item1.weight"])
          setItem1Weight(map["ourproducts.overview.item1.weight"]);
        if (map["ourproducts.overview.item1.content"])
          setItem1Content(map["ourproducts.overview.item1.content"]);
        if (map["ourproducts.overview.item1.content_weight"])
          setItem1ContentWeight(map["ourproducts.overview.item1.content_weight"]);
        if (map["ourproducts.overview.item1.image"])
          setItem1Image(map["ourproducts.overview.item1.image"]);

        if (map["ourproducts.overview.item2.title"])
          setItem2Title(map["ourproducts.overview.item2.title"]);
        if (map["ourproducts.overview.item2.weight"])
          setItem2Weight(map["ourproducts.overview.item2.weight"]);
        if (map["ourproducts.overview.item2.content"])
          setItem2Content(map["ourproducts.overview.item2.content"]);
        if (map["ourproducts.overview.item2.content_weight"])
          setItem2ContentWeight(map["ourproducts.overview.item2.content_weight"]);
        if (map["ourproducts.overview.item2.image"])
          setItem2Image(map["ourproducts.overview.item2.image"]);

        if (map["ourproducts.overview.item3.title"])
          setItem3Title(map["ourproducts.overview.item3.title"]);
        if (map["ourproducts.overview.item3.weight"])
          setItem3Weight(map["ourproducts.overview.item3.weight"]);
        if (map["ourproducts.overview.item3.content"])
          setItem3Content(map["ourproducts.overview.item3.content"]);
        if (map["ourproducts.overview.item3.content_weight"])
          setItem3ContentWeight(map["ourproducts.overview.item3.content_weight"]);
        if (map["ourproducts.overview.item3.image"])
          setItem3Image(map["ourproducts.overview.item3.image"]);

        if (map["ourproducts.overview.item4.title"])
          setItem4Title(map["ourproducts.overview.item4.title"]);
        if (map["ourproducts.overview.item4.weight"])
          setItem4Weight(map["ourproducts.overview.item4.weight"]);
        if (map["ourproducts.overview.item4.content"])
          setItem4Content(map["ourproducts.overview.item4.content"]);
        if (map["ourproducts.overview.item4.content_weight"])
          setItem4ContentWeight(map["ourproducts.overview.item4.content_weight"]);
        if (map["ourproducts.overview.item4.image"])
          setItem4Image(map["ourproducts.overview.item4.image"]);
      } catch (err) {
        console.warn("[OurProducts] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  return (
    <Box className="our-products-wrap2" sx={{
      py: 8
    }}>
      <Container className="container">
        <Typography
          variant="h2"
          data-aos="fade-up"
          data-cms-key="ourproducts.overview.title"
          sx={{
            mb: 8,
            whiteSpace: { xs: "normal", lg: "pre-line" },
            textAlign: "center"
          }}>
          {title}
        </Typography>
        <Box
          sx={{
            position: "relative",
            ".swiper-button-next, .swiper-button-prev": {
              backgroundColor: "black",
              color: "white",
              borderRadius: "50%",
              width: 40,
              height: 40,
              padding: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
              "&::after": {
                fontSize: "1.2rem",
                fontWeight: "bold",
              },
            },
            ".swiper-button-prev": { left: 0 },
            ".swiper-button-next": { right: 0 },
          }}
        >
          <Swiper
            spaceBetween={24}
            rewind
            autoplay={{ delay: 5000 }}
            modules={[Autoplay, Navigation]}
            breakpoints={{
              // mobile
              0: {
                slidesPerView: 1,
              },
              // tablet
              600: {
                slidesPerView: 2,
              },
              // desktop
              1200: {
                slidesPerView: 4,
              },
            }}
            navigation
          >
          {/* Item 1 */}
          <SwiperSlide>
            <Card className="our-products-card" data-aos="fade-up">
              <Box data-aos="fade-up" sx={{
                mt: "auto"
              }}>
                <Image
                  src={item1Image}
                  title="Premium Tapioca Starch - Jumbo Bags 850 Kg"
                  alt="Premium Tapioca Starch - Jumbo Bags 850 Kg"
                  width={330}
                  height={359}
                  className="img-fluid"
                  data-cms-key="ourproducts.overview.item1.image"
                />
              </Box>
              <Box className="our-products-card-inner">
                <CardHeader
                  title={item1Title}
                  subheader={item1Weight}
                  titleTypographyProps={{
                    "data-cms-key": "ourproducts.overview.item1.title",
                  }}
                  subheaderTypographyProps={{
                    "data-cms-key": "ourproducts.overview.item1.weight",
                  }}
                />
                <CardContent data-aos="fade-up" data-aos-offset="0">
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600 }}
                    data-cms-key="ourproducts.overview.item1.content"
                  >
                    {item1Content}
                  </Typography>
                  <Typography
                    variant="body1"
                    data-cms-key="ourproducts.overview.item1.content_weight"
                  >
                    {item1ContentWeight}
                  </Typography>
                </CardContent>
              </Box>
            </Card>
          </SwiperSlide>

          {/* Item 2 */}
          <SwiperSlide>
            <Card className="our-products-card" data-aos="fade-up">
              <Box data-aos="fade-up" sx={{
                mb: "auto"
              }}>
                <Image
                  src={item2Image}
                  title="Premium Tapioca Starch - PP/PE Bags 50 Kg"
                  alt="Premium Tapioca Starch - PP/PE Bags 50 Kg"
                  width={330}
                  height={359}
                  className="img-fluid"
                  data-cms-key="ourproducts.overview.item2.image"
                />
              </Box>
              <Box className="our-products-card-inner">
                <CardHeader
                  title={item2Title}
                  subheader={item2Weight}
                  titleTypographyProps={{
                    "data-cms-key": "ourproducts.overview.item2.title",
                  }}
                  subheaderTypographyProps={{
                    "data-cms-key": "ourproducts.overview.item2.weight",
                  }}
                />
                <CardContent data-aos="fade-up" data-aos-offset="0">
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600 }}
                    data-cms-key="ourproducts.overview.item2.content"
                  >
                    {item2Content}
                  </Typography>
                  <Typography
                    variant="body1"
                    data-cms-key="ourproducts.overview.item2.content_weight"
                  >
                    {item2ContentWeight}
                  </Typography>
                </CardContent>
              </Box>
            </Card>
          </SwiperSlide>

          {/* Item 3 */}
          <SwiperSlide>
            <Card className="our-products-card" data-aos="fade-up">
              <Box data-aos="fade-up" sx={{
                mt: "auto"
              }}>
                <Image
                  src={item3Image}
                  title="Premium Tapioca Starch - PP/PE Bags 25 Kg"
                  alt="Premium Tapioca Starch - PP/PE Bags 25 Kg"
                  width={330}
                  height={359}
                  className="img-fluid"
                  data-cms-key="ourproducts.overview.item3.image"
                />
              </Box>
              <Box className="our-products-card-inner">
                <CardHeader
                  title={item3Title}
                  subheader={item3Weight}
                  titleTypographyProps={{
                    "data-cms-key": "ourproducts.overview.item3.title",
                  }}
                  subheaderTypographyProps={{
                    "data-cms-key": "ourproducts.overview.item3.weight",
                  }}
                />
                <CardContent data-aos="fade-up" data-aos-offset="0">
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600 }}
                    data-cms-key="ourproducts.overview.item3.content"
                  >
                    {item3Content}
                  </Typography>
                  <Typography
                    variant="body1"
                    data-cms-key="ourproducts.overview.item3.content_weight"
                  >
                    {item3ContentWeight}
                  </Typography>
                </CardContent>
              </Box>
            </Card>
          </SwiperSlide>

          {/* Item 4 */}
          <SwiperSlide>
            <Card className="our-products-card" data-aos="fade-up">
              <Box sx={{
                mt: "auto"
              }}>
                <Image
                  src={item4Image}
                  title="Premium Tapioca Starch - Paper Bags 25 Kg"
                  alt="Premium Tapioca Starch - Paper Bags 25 Kg"
                  width={330}
                  height={359}
                  className="img-fluid"
                  data-cms-key="ourproducts.overview.item4.image"
                />
              </Box>
              <Box className="our-products-card-inner">
                <CardHeader
                  title={item4Title}
                  subheader={item4Weight}
                  titleTypographyProps={{
                    "data-cms-key": "ourproducts.overview.item4.title",
                  }}
                  subheaderTypographyProps={{
                    "data-cms-key": "ourproducts.overview.item4.weight",
                  }}
                />
                <CardContent data-aos="fade-up" data-aos-offset="0">
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600 }}
                    data-cms-key="ourproducts.overview.item4.content"
                  >
                    {item4Content}
                  </Typography>
                  <Typography
                    variant="body1"
                    data-cms-key="ourproducts.overview.item4.content_weight"
                  >
                    {item4ContentWeight}
                  </Typography>
                </CardContent>
              </Box>
            </Card>
          </SwiperSlide>
          </Swiper>
        </Box>
      </Container>
    </Box>
  );
}